import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import Parceiro from '../classes/Parceiro';
import ParceiroDataSet from '../data/parceiroDataset';
import Usuario from '../classes/Usuario';
import UsuarioDataSet from '../data/UsuarioDataSet';
import { UserContext } from '../../App';

interface PartnerSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onPartnerSelected: () => void;
}

/**
 * Modal amigável para solicitar seleção de parceiro
 * Aparece automaticamente quando usuário tenta acessar funcionalidade que requer parceiro
 */
const PartnerSelectionDialog: React.FC<PartnerSelectionDialogProps> = ({
  open,
  onClose,
  onPartnerSelected,
}) => {
  const { t } = useTranslation();
  const user: any = React.useContext(UserContext);
  const [parceiros, setParceiros] = useState<Parceiro[]>([]);
  const [selectedParceiro, setSelectedParceiro] = useState<Parceiro | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const dataSet = new ParceiroDataSet();
  const userDataSet = new UsuarioDataSet();

  useEffect(() => {
    if (!open) return;

    const loadParceiros = async () => {
      setIsLoadingData(true);
      try {
        let response: Parceiro[];
        
        if (user.isRoot) {
          response = await dataSet.ListarAtivos();
        } else {
          const storageUser: Usuario = JSON.parse(
            String(localStorage.getItem(userDataSet.getKeyCach()))
          );
          response = await dataSet.ListarAtivosPorUsuario(+storageUser.id);
        }
        
        setParceiros(response);
        dataSet.AdicionarListaAoCache(response);
      } catch (error) {
        console.error('Erro ao carregar parceiros:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadParceiros();
  }, [open]);

  const handleContinue = () => {
    if (selectedParceiro) {
      setIsLoading(true);
      
      // Salvar no cache
      dataSet.AdicionarCodigoFornecedorSapAoCache(Number(selectedParceiro.codigoFornecedorSAP));
      dataSet.AdicionarGrupoAoCache(String(selectedParceiro.apelido));
      dataSet.AdicionarIdAoCache(Number(selectedParceiro.id));
      dataSet.AdicionarObjetoAoCache(selectedParceiro);
      
      // Disparar evento
      window.dispatchEvent(new Event('partnerSelected'));
      
      // Aguardar um pouco e recarregar
      setTimeout(() => {
        onPartnerSelected();
        window.location.reload();
      }, 300);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevenir fechar clicando fora ou ESC
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="warning" fontSize="large" />
          <Typography variant="h6" fontWeight={600}>
            {t('global.message.partnerSelectionRequired') || 'Seleção de Parceiro Necessária'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              {t('global.message.selectPartnerToContinue') || 
                'Para acessar esta funcionalidade, você precisa selecionar um parceiro primeiro.'}
            </Typography>
          </Alert>

          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('global.message.selectPartnerBelow') || 
                'Selecione um parceiro abaixo para continuar:'}
            </Typography>

            <Autocomplete
              loading={isLoadingData}
              disabled={isLoading}
              noOptionsText={t("pages.partners.text.autoCompleteNoOptions") || "Nenhuma opção"}
              options={parceiros}
              getOptionLabel={(option) => `${option.codigoFornecedorSAP} - ${option.grupo}`}
              value={selectedParceiro}
              onChange={(event, newValue) => {
                setSelectedParceiro(newValue);
              }}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('header.partner') || 'Parceiro'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingData ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {selectedParceiro && (
            <Alert severity="success" icon={!isLoading ? undefined : <CircularProgress size={20} />}>
              <Typography variant="body2">
                {isLoading 
                  ? 'Carregando...' 
                  : '✓ Parceiro selecionado! Clique em "Continuar" para prosseguir.'
                }
              </Typography>
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('global.message.partnerSelectionHint') || 
              '💡 Dica: Você pode trocar de parceiro a qualquer momento clicando no ícone ao lado do nome do parceiro no cabeçalho.'}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isLoading}>
          {t('common.cancel') || 'Cancelar'}
        </Button>
        <Button
          onClick={handleContinue}
          variant="contained"
          color="primary"
          disabled={!selectedParceiro || isLoading || isLoadingData}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {isLoading ? 'Carregando...' : (t('common.continue') || 'Continuar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerSelectionDialog;
